<template>
    <div class="px-8 py-6 relative">
        <!-- Elegant Step Indicator -->
        <div class="flex items-center justify-center mb-5">
            <div></div>
            <div class="flex items-center space-x-3">
                <div @click="currentStep = 1" class="cursor-pointer flex items-center group">
                    <div
                        :class="['w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300', currentStep === 1 ? 'bg-minplan-greybeige text-white shadow-lg ring-4 ring-minplan-greybeige/20' : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200']">
                        1
                    </div>
                    <span
                        :class="['ml-3 text-sm font-bold tracking-wide uppercase', currentStep === 1 ? 'text-minplan-greybeige' : 'text-gray-400']">{{
                            $t('agencies.agency') }}</span>
                </div>
                <div class="w-16 h-0.5 bg-gray-100 rounded-full mx-4 overflow-hidden">
                    <div
                        :class="['h-full bg-minplan-greybeige rounded-full transition-all duration-500', currentStep >= 2 ? 'w-full' : 'w-0']">
                    </div>
                </div>
                <div @click="currentStep = 2" class="cursor-pointer flex items-center group">
                    <div
                        :class="['w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300', currentStep === 2 ? 'bg-minplan-greybeige text-white shadow-lg ring-4 ring-minplan-greybeige/20' : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200']">
                        2
                    </div>
                    <span
                        :class="['ml-3 text-sm font-bold tracking-wide uppercase', currentStep === 2 ? 'text-minplan-greybeige' : 'text-gray-400']">{{
                            $t('agencies.branding') }}</span>
                </div>
            </div>
            
            <button @click="cancel" type="button" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
            </button>
        </div>

        <form @submit.prevent="submit">
            <!-- Step 1: Agency Details -->
            <div v-show="currentStep === 1" class="animate-fade-in">
                <div class="mb-8">
                    <h2 class="text-2xl font-bold text-gray-900 mb-1">Agency</h2>
                </div>

                <div class="space-y-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        <div class="col-span-2">
                            <label for="name" class="block text-sm font-bold text-gray-700 mb-1">Company <span
                                    class="text-red-500">*</span></label>
                            <input type="text" name="name" id="name" v-model="form.name"
                                class="w-full h-11 px-4 focus:ring-2 focus:ring-minplan-greybeige focus:border-minplan-greybeige block shadow-sm sm:text-sm border-gray-300 rounded-xl transition-all" />
                            <p class="mt-1.5 text-[11px] text-gray-400 italic">Name must be filled in.</p>
                            <jet-input-error :message="form.errors.name" class="mt-2" />
                        </div>

                        <div>
                            <label for="ownerFirstname" class="block text-sm font-bold text-gray-700 mb-1">First name
                                <span class="text-red-500">*</span></label>
                            <input type="text" name="ownerFirstname" id="ownerFirstname" v-model="form.ownerFirstname"
                                class="w-full h-11 px-4 focus:ring-2 focus:ring-minplan-greybeige focus:border-minplan-greybeige block shadow-sm sm:text-sm border-gray-300 rounded-xl transition-all" />
                            <p class="mt-1.5 text-[11px] text-gray-400 italic">First name field must be filled in.</p>
                            <jet-input-error :message="form.errors.ownerFirstname" class="mt-2" />
                        </div>

                        <div>
                            <label for="ownerLastname" class="block text-sm font-bold text-gray-700 mb-1">Last name
                                <span class="text-red-500">*</span></label>
                            <input type="text" name="ownerLastname" id="ownerLastname" v-model="form.ownerLastname"
                                class="w-full h-11 px-4 focus:ring-2 focus:ring-minplan-greybeige focus:border-minplan-greybeige block shadow-sm sm:text-sm border-gray-300 rounded-xl transition-all" />
                            <p class="mt-1.5 text-[11px] text-gray-400 italic">Last name must be filled in.</p>
                            <jet-input-error :message="form.errors.ownerLastname" class="mt-2" />
                        </div>

                        <div>
                            <label for="ownerEmail" class="block text-sm font-bold text-gray-700 mb-1">Email <span
                                    class="text-red-500">*</span></label>
                            <input type="email" name="ownerEmail" id="ownerEmail" v-model="form.ownerEmail"
                                class="w-full h-11 px-4 focus:ring-2 focus:ring-minplan-greybeige focus:border-minplan-greybeige block shadow-sm sm:text-sm border-gray-300 rounded-xl transition-all" />
                            <p class="mt-1.5 text-[11px] text-gray-400 italic">The email address must be filled out.</p>
                            <jet-input-error :message="form.errors.ownerEmail" class="mt-2" />
                        </div>

                        <div>
                            <label for="ownerMobile" class="block text-sm font-bold text-gray-700 mb-1">Mobile <span
                                    class="text-red-500">*</span></label>
                            <input type="text" name="ownerMobile" id="ownerMobile" v-model="form.ownerMobile"
                                :maxlength="$page.props.branding.mobile_number_max_digit || 12"
                                @input="form.ownerMobile = form.ownerMobile.replace(/[^0-9]/g, '').slice(0, $page.props.branding.mobile_number_max_digit || 12)"
                                class="w-full h-11 px-4 focus:ring-2 focus:ring-minplan-greybeige focus:border-minplan-greybeige block shadow-sm sm:text-sm border-gray-300 rounded-xl transition-all" />
                            <p class="mt-1.5 text-[11px] text-gray-400 italic">Mobile must be filled out (max {{
                                $page.props.branding.mobile_number_max_digit || 12 }} digits, numeric only).</p>
                            <jet-input-error :message="form.errors.ownerMobile" class="mt-2" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Step 2: Branding -->
            <div v-show="currentStep === 2" class="animate-fade-in space-y-8">
                <div>
                    <h2 class="text-2xl font-bold text-gray-900 mb-1">Branding settings (Optional)</h2>
                </div>

                <div class="flex flex-col lg:flex-row gap-10" style="margin-top: 0px;">
                    <!-- Preview Card -->
                    <div class="lg:w-2/5">
                        <div class="sticky top-6">
                            <h3 class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Preview</h3>
                            <branding-preview :navbar-color="form.branding_navbar_color"
                                :sidebar-color="form.branding_sidebar_color"
                                :navbar-text-color="form.branding_navbar_text_color"
                                :sidebar-text-color="form.branding_sidebar_text_color" :logo-url="logoPreviewUrl" />
                        </div>
                    </div>

                    <!-- Input Card -->
                    <div class="lg:w-3/5 space-y-8">
                        <div>
                            <label class="block text-sm font-bold text-minplan-greybeige mb-4 uppercase tracking-wider">Logo
                                Preview</label>
                            <div class="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                                <div class="relative group">
                                    <div v-if="logoPreviewUrl"
                                        class="p-3 bg-gray-50 rounded-2xl border-2 border-gray-200 w-[240px] h-[120px] flex items-center justify-center overflow-hidden transition-all group-hover:border-minplan-greybeige shadow-sm">
                                        <img :src="logoPreviewUrl" class="max-w-full max-h-full object-contain"
                                            alt="Logo Preview" />
                                    </div>
                                    <div v-else
                                        class="w-[240px] h-[120px] bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400">
                                        <svg class="w-10 h-10 opacity-20" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z">
                                            </path>
                                        </svg>
                                    </div>
                                </div>
                                <div class="flex-1 w-full">
                                    <label
                                        class="flex flex-col items-center justify-center w-full h-[80px] border-2 border-gray-200 border-dashed rounded-2xl cursor-pointer bg-gray-50 hover:bg-white hover:border-minplan-greybeige hover:shadow-md transition-all group">
                                        <div class="flex flex-col items-center justify-center pt-2 pb-2 text-center">
                                            <p class="text-[11px] font-bold text-minplan-greybeige mb-0.5">Agency Logo</p>
                                            <p class="text-[11px] text-gray-500 font-medium whitespace-nowrap"><span
                                                    class="font-bold underline text-minplan-greybeige">click_to_upload</span> or_drag_and_drop
                                            </p>
                                        </div>
                                        <input type="file" ref="logoInput" class="hidden" @change="handleFileUpload"
                                            accept="image/png,image/jpeg,image/jpg,image/svg+xml" />
                                    </label>
                                    <p class="mt-2 text-[10px] text-gray-400 leading-relaxed">Allowed formats: PNG, JPG,
                                        JPEG, SVG, Max 5 MB. Recommended dimensions: 240x120 pixels.</p>
                                </div>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-100">
                            <!-- Navbar Color -->
                            <div class="space-y-2">
                                <label
                                    class="block text-[11px] font-bold text-gray-500 uppercase tracking-widest">Navigation
                                    color</label>
                                <div class="flex items-center space-x-3">
                                    <div class="relative flex-1">
                                        <input type="text" v-model="form.branding_navbar_color"
                                            class="w-full h-11 pl-8 focus:ring-2 focus:ring-minplan-greybeige border-gray-300 rounded-xl text-sm font-mono"
                                            placeholder="#RRGGBB" />
                                        <div
                                            class="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400 font-mono">
                                            #</div>
                                    </div>
                                    <input type="color" v-model="form.branding_navbar_color"
                                        class="h-11 w-14 p-1.5 border border-gray-300 rounded-xl cursor-pointer bg-white shadow-sm" />
                                </div>
                            </div>

                            <!-- Sidebar Color -->
                            <div class="space-y-2">
                                <label
                                    class="block text-[11px] font-bold text-gray-500 uppercase tracking-widest">Sidebar
                                    color</label>
                                <div class="flex items-center space-x-3">
                                    <div class="relative flex-1">
                                        <input type="text" v-model="form.branding_sidebar_color"
                                            class="w-full h-11 pl-8 focus:ring-2 focus:ring-minplan-greybeige border-gray-300 rounded-xl text-sm font-mono"
                                            placeholder="#RRGGBB" />
                                        <div
                                            class="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400 font-mono">
                                            #</div>
                                    </div>
                                    <input type="color" v-model="form.branding_sidebar_color"
                                        class="h-11 w-14 p-1.5 border border-gray-300 rounded-xl cursor-pointer bg-white shadow-sm" />
                                </div>
                            </div>

                            <!-- Navbar Text -->
                            <div class="space-y-2">
                                <label
                                    class="block text-[11px] font-bold text-gray-500 uppercase tracking-widest">Navbar
                                    Text Color</label>
                                <div class="flex items-center space-x-3">
                                    <div class="relative flex-1">
                                        <input type="text" v-model="form.branding_navbar_text_color"
                                            class="w-full h-11 pl-8 focus:ring-2 focus:ring-minplan-greybeige border-gray-300 rounded-xl text-sm font-mono"
                                            placeholder="#RRGGBB" />
                                        <div
                                            class="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400 font-mono">
                                            #</div>
                                    </div>
                                    <input type="color" v-model="form.branding_navbar_text_color"
                                        class="h-11 w-14 p-1.5 border border-gray-300 rounded-xl cursor-pointer bg-white shadow-sm" />
                                </div>
                            </div>

                            <!-- Sidebar Text -->
                            <div class="space-y-2">
                                <label
                                    class="block text-[11px] font-bold text-gray-500 uppercase tracking-widest">Sidebar
                                    Text Color</label>
                                <div class="flex items-center space-x-3">
                                    <div class="relative flex-1">
                                        <input type="text" v-model="form.branding_sidebar_text_color"
                                            class="w-full h-11 pl-8 focus:ring-2 focus:ring-minplan-greybeige border-gray-300 rounded-xl text-sm font-mono"
                                            placeholder="#RRGGBB" />
                                        <div
                                            class="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400 font-mono">
                                            #</div>
                                    </div>
                                    <input type="color" v-model="form.branding_sidebar_text_color"
                                        class="h-11 w-14 p-1.5 border border-gray-300 rounded-xl cursor-pointer bg-white shadow-sm" />
                                </div>
                            </div>

                            <!-- Email Background -->
                            <div class="space-y-2">
                                <label class="block text-[11px] font-bold text-gray-500 uppercase tracking-widest">Email
                                    background color</label>
                                <div class="flex items-center space-x-3">
                                    <div class="relative flex-1">
                                        <input type="text" v-model="form.branding_email_background_color"
                                            class="w-full h-11 pl-8 focus:ring-2 focus:ring-minplan-greybeige border-gray-300 rounded-xl text-sm font-mono"
                                            placeholder="#RRGGBB" />
                                        <div
                                            class="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400 font-mono">
                                            #</div>
                                    </div>
                                    <input type="color" v-model="form.branding_email_background_color"
                                        class="h-11 w-14 p-1.5 border border-gray-300 rounded-xl cursor-pointer bg-white shadow-sm" />
                                </div>
                            </div>
                            <div class="space-y-2 pt-5">
                                <button @click="resetToDefault" type="button"
                                    class="text-xs font-bold text-minplan-greybeige hover:opacity-80 transition-opacity uppercase tracking-widest flex items-center mt-5">
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15">
                                        </path>
                                    </svg>
                                    Reset to default
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <!-- Enhanced Footer -->
            <div
                class="mt-5 flex justify-between items-center bg-gray-50 -mx-8 -mb-6 px-10 py-6 border-t border-gray-100">
                <button @click="cancel" type="button"
                    class="text-xs font-bold text-gray-400 hover:text-gray-600 uppercase tracking-widest transition-colors">
                    Cancel
                </button>

                <div class="flex items-center space-x-4">
                    <secondary-button v-if="currentStep === 2" @click="currentStep = 1" type="button"
                        class="h-11 px-6 rounded-xl text-xs font-bold uppercase tracking-widest">
                        Previous
                    </secondary-button>

                    <button v-if="currentStep === 1" @click="currentStep = 2" type="button"
                        class="h-11 px-8 rounded-xl bg-minplan-greybeige text-white text-xs font-bold uppercase tracking-widest hover:opacity-90 shadow-md shadow-gray-200 transition-all flex items-center">
                        Next
                        <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7">
                            </path>
                        </svg>
                    </button>

                    <primary-button v-if="currentStep === 2" :class="{ 'opacity-25': form.processing }"
                        :disabled="form.processing"
                        class="h-11 px-10 rounded-xl bg-minplan-greybeige text-white text-xs font-bold uppercase tracking-widest hover:opacity-90 shadow-lg shadow-gray-200 transition-all">
                        Save
                    </primary-button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { defineComponent, ref, onUnmounted } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import JetInputError from "@/Jetstream/InputError";
import PageTitle from "@/Shared/Components/PageTitle";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import FormCard from "@/Shared/Components/FormCard";
import RequiredMark from "@/Shared/Components/RequiredMark";
import BrandingPreview from "@/Shared/Components/BrandingPreview";
import { notify } from "notiwind";

export default defineComponent({
    maxWidth: "5xl",
    props: ["globalBranding"],
    layout: MainLayout,
    emits: ["closeModal"],
    components: {
        PageTitle,
        PrimaryButton,
        SecondaryButton,
        JetInputError,
        FormCard,
        RequiredMark,
        BrandingPreview
    },
    setup(props, { emit }) {
        const currentStep = ref(1);
        const logoPreviewUrl = ref(null);
        const { props: pageProps } = usePage();

        const getLogoUrl = (path) => {
            if (!path) return "";
            return window.route("media.restore", { filename: path });
        };

        const form = useForm({
            name: null,
            ownerFirstname: null,
            ownerLastname: null,
            ownerEmail: null,
            ownerMobile: null,
            branding_logo_path: null,
            branding_navbar_color: props.globalBranding?.navbar_color || "#2f3441",
            branding_sidebar_color: props.globalBranding?.sidebar_color || "#2f3441",
            branding_navbar_text_color: props.globalBranding?.navbar_text_color || "#ffffff",
            branding_sidebar_text_color: props.globalBranding?.sidebar_text_color || "#ffffff",
            branding_email_background_color: props.globalBranding?.email_background_color || "#f3f4f6",
        });

        onUnmounted(() => {
            if (logoPreviewUrl.value) URL.revokeObjectURL(logoPreviewUrl.value);
        });

        const handleFileUpload = (event) => {
            const file = event.target.files[0];
            if (file) {
                if (file.size > 5 * 1024 * 1024) {
                    notify({ group: "error", title: "Error", text: "File too large (max 5MB)" }, 3000);
                    event.target.value = "";
                    return;
                }
                form.branding_logo_path = file;
                logoPreviewUrl.value = URL.createObjectURL(file);
            }
        };

        const resetToDefault = () => {
            form.branding_navbar_color = props.globalBranding?.navbar_color || "#2f3441";
            form.branding_sidebar_color = props.globalBranding?.sidebar_color || "#2f3441";
            form.branding_navbar_text_color = props.globalBranding?.navbar_text_color || "#ffffff";
            form.branding_sidebar_text_color = props.globalBranding?.sidebar_text_color || "#ffffff";
            form.branding_email_background_color = props.globalBranding?.email_background_color || "#f3f4f6";
            form.branding_logo_path = null;
            logoPreviewUrl.value = null;
        };

        const cancel = () => {
            emit("closeModal");
        };

        const submit = () => {
            form.post(window.route("admin.agencies.store"), {
                preserveScroll: true,
                onSuccess: () => {
                    notify({
                        group: "success",
                        title: 'Success',
                        text: 'Agency created and published successfully'
                    });
                    emit("closeModal");
                },
                onError: (errors) => {
                    const step1Fields = ['name', 'ownerFirstname', 'ownerLastname', 'ownerEmail', 'ownerMobile'];
                    const hasStep1Error = step1Fields.some(field => Object.keys(errors).includes(field));
                    if (hasStep1Error) {
                        currentStep.value = 1;
                    }
                }
            });
        };

        return {
            currentStep,
            form,
            logoPreviewUrl,
            handleFileUpload,
            resetToDefault,
            cancel,
            submit
        };
    }
});
</script>

<style scoped>
.animate-fade-in {
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
